import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, CheckCircle, AlertTriangle, ExternalLink, Store, Link2, Shield, Key, Eye, EyeOff, Info, Trash2, Package, Wallet, Clock, ArrowDownCircle, User, Building2, Pencil, Sparkles, Linkedin, Instagram, Twitter, Globe, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency, type Currency } from "@/lib/currency";
import { invokeShopifyEmbeddedFunction } from "@/lib/shopifySessionToken";
import { openShopifyTopLevel } from "@/lib/shopifyEmbedded";
import { GST_MODULE_ENABLED } from "@/lib/featureFlags";

interface MerchantSettingsProps {
  merchantId: string;
  storeDomain: string;
  storeName: string;
  canEdit: boolean;
  isSuperAdmin: boolean;
  currentPlan?: string;
  onNavigateToBilling?: () => void;
}

interface UninstallStats {
  totalRequests: number;
  completedRequests: number;
  pendingRequests: number;
  totalRevenue: number;
  pendingRevenue: number;
}

interface BusinessInfo {
  business_email: string;
  business_phone: string;
  business_address: string;
  gst_number: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone: string;
  store_category: string;
}

interface SocialInfo {
  linkedin_url: string;
  instagram_url: string;
  twitter_url: string;
  website_url: string;
  email_signature: string;
}

const STORE_CATEGORIES: { value: string; label: string }[] = [
  { value: "fashion_apparel", label: "Fashion & Apparel" },
  { value: "health_wellness", label: "Health & Wellness" },
  { value: "beauty_cosmetics", label: "Beauty & Cosmetics" },
  { value: "electronics", label: "Electronics" },
  { value: "food_beverages", label: "Food & Beverages" },
  { value: "home_decor", label: "Home Decor" },
  { value: "pet_supplies", label: "Pet Supplies" },
];

const MerchantSettings = ({ merchantId, storeDomain, storeName, canEdit, isSuperAdmin, currentPlan, onNavigateToBilling }: MerchantSettingsProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasToken, setHasToken] = useState(false);
  const [widgetInstalled, setWidgetInstalled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [connecting, setConnecting] = useState(false);
  
  // Splitkart payment provider state
  const [splitkartPaymentProvider, setSplitkartPaymentProvider] = useState<string>("razorpay");
  const [merchantCurrency, setMerchantCurrency] = useState<Currency>("USD");
  const [savingPaymentProvider, setSavingPaymentProvider] = useState(false);
  
  // Business info state
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    business_email: "",
    business_phone: "",
    business_address: "",
    gst_number: "",
    contact_person_name: "",
    contact_person_email: "",
    contact_person_phone: "",
    store_category: "",
  });
  const [editingBusinessInfo, setEditingBusinessInfo] = useState(false);
  const [savingBusinessInfo, setSavingBusinessInfo] = useState(false);
  
  // Social info state (for professionals)
  const [socialInfo, setSocialInfo] = useState<SocialInfo>({
    linkedin_url: "",
    instagram_url: "",
    twitter_url: "",
    website_url: "",
    email_signature: "",
  });
  const [editingSocialInfo, setEditingSocialInfo] = useState(false);
  const [savingSocialInfo, setSavingSocialInfo] = useState(false);
  
  // Uninstall state
  const [showUninstallDialog, setShowUninstallDialog] = useState(false);
  const [uninstallStats, setUninstallStats] = useState<UninstallStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [uninstallingWidget, setUninstallingWidget] = useState(false);
  
  // Manual token entry state
  const [manualToken, setManualToken] = useState("");
  const [shopifyStoreDomainInput, setShopifyStoreDomainInput] = useState(() => {
    if (typeof storeDomain === "string" && storeDomain.includes(".myshopify.com")) {
      return storeDomain;
    }
    return "";
  });
  const [showManualToken, setShowManualToken] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  

  useEffect(() => {
    checkExistingToken();
    fetchBusinessInfo();
  }, [merchantId]);

  // Listen for OAuth popup success message
  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      if (event.data?.type === 'shopify_oauth_success') {
        console.log("[Shakatah] OAuth success message received:", event.data);
        setConnecting(false);
        setHasToken(true);
        toast.success(`Store "${event.data.shop}" reconnected successfully!`);
        // Refresh the merchant data
        checkExistingToken();
        fetchBusinessInfo();
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .eq('id', merchantId)
        .single();

      if (!error && data) {
        const merchantData = data as any;
        setBusinessInfo({
          business_email: merchantData.business_email || "",
          business_phone: merchantData.business_phone || "",
          business_address: merchantData.business_address || "",
          gst_number: merchantData.gst_number || "",
          contact_person_name: merchantData.contact_person_name || "",
          contact_person_email: merchantData.contact_person_email || "",
          contact_person_phone: merchantData.contact_person_phone || "",
          store_category: merchantData.store_category || "",
        });
        // Also load social info
        setSocialInfo({
          linkedin_url: merchantData.linkedin_url || "",
          instagram_url: merchantData.instagram_url || "",
          twitter_url: merchantData.twitter_url || "",
          website_url: merchantData.website_url || "",
          email_signature: merchantData.email_signature || "",
        });
        // Load Splitkart payment provider
        setSplitkartPaymentProvider(merchantData.splitkart_payment_provider || "razorpay");
        setMerchantCurrency((merchantData.currency as Currency) || "USD");
      }
    } catch (error) {
      console.error("Error fetching business info:", error);
    }
  };

  const handleSavePaymentProvider = async (provider: string) => {
    setSavingPaymentProvider(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({ splitkart_payment_provider: provider })
        .eq('id', merchantId);

      if (error) throw error;

      setSplitkartPaymentProvider(provider);
      toast.success(`Default payment provider set to ${provider === 'razorpay' ? 'Razorpay' : 'Stripe'}`);
    } catch (error: any) {
      console.error("Error saving payment provider:", error);
      toast.error("Failed to save payment provider");
    } finally {
      setSavingPaymentProvider(false);
    }
  };

  const handleSaveSocialInfo = async () => {
    setSavingSocialInfo(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({
          linkedin_url: socialInfo.linkedin_url || null,
          instagram_url: socialInfo.instagram_url || null,
          twitter_url: socialInfo.twitter_url || null,
          website_url: socialInfo.website_url || null,
          email_signature: socialInfo.email_signature || null,
        })
        .eq('id', merchantId);

      if (error) throw error;

      toast.success("Social links and signature saved!");
      setEditingSocialInfo(false);
    } catch (error: any) {
      console.error("Error saving social info:", error);
      toast.error("Failed to save changes");
    } finally {
      setSavingSocialInfo(false);
    }
  };

  const handleSaveBusinessInfo = async () => {
    if (!businessInfo.contact_person_name || !businessInfo.contact_person_email) {
      toast.error("Contact name and email are required");
      return;
    }

    setSavingBusinessInfo(true);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({
          business_email: businessInfo.business_email || null,
          business_phone: businessInfo.business_phone || null,
          business_address: businessInfo.business_address || null,
          gst_number: businessInfo.gst_number || null,
          contact_person_name: businessInfo.contact_person_name,
          contact_person_email: businessInfo.contact_person_email,
          contact_person_phone: businessInfo.contact_person_phone || null,
          store_category: businessInfo.store_category || null,
        } as any)
        .eq('id', merchantId);

      if (error) throw error;

      toast.success("Business information updated!");
      setEditingBusinessInfo(false);
    } catch (error) {
      console.error("Error saving business info:", error);
      toast.error("Failed to save business information");
    } finally {
      setSavingBusinessInfo(false);
    }
  };

  const getCategoryLabel = (value: string) => {
    const category = STORE_CATEGORIES.find(c => c.value === value);
    return category ? category.label : value; // If not found in predefined list, show the value (custom category)
  };

  const checkExistingToken = async () => {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('shopify_access_token, widget_installed')
        .eq('id', merchantId)
        .single();

      if (!error && data) {
        if (data.shopify_access_token) {
          setHasToken(true);
        }
        setWidgetInstalled(data.widget_installed ?? false);
      }
    } catch (error) {
      console.error("Error checking token:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUninstallStats = async () => {
    setLoadingStats(true);
    try {
      const { data: requests, error } = await supabase
        .from('split_payment_requests')
        .select('status, total_price')
        .eq('merchant_id', merchantId);

      if (error) throw error;

      const stats: UninstallStats = {
        totalRequests: requests?.length || 0,
        completedRequests: requests?.filter(r => r.status === 'completed').length || 0,
        pendingRequests: requests?.filter(r => r.status === 'pending').length || 0,
        totalRevenue: requests?.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.total_price || 0), 0) || 0,
        pendingRevenue: requests?.filter(r => r.status === 'pending').reduce((sum, r) => sum + (r.total_price || 0), 0) || 0,
      };

      setUninstallStats(stats);
    } catch (error) {
      console.error("Error fetching uninstall stats:", error);
      setUninstallStats({
        totalRequests: 0,
        completedRequests: 0,
        pendingRequests: 0,
        totalRevenue: 0,
        pendingRevenue: 0,
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const handleUninstallClick = async () => {
    await fetchUninstallStats();
    setShowUninstallDialog(true);
  };

  const handleUninstallWidget = async () => {
    setUninstallingWidget(true);
    try {
      const { data, error } = await invokeShopifyEmbeddedFunction('install-widget', {
        body: { 
          merchantId,
          action: 'uninstall',
        },
      });

      if (error) throw error;
      
      if (data?.success) {
        if (data.requiresThemeExtension) {
          toast.success(data.message || "Disable the app embed from Shopify Theme Editor to remove storefront widgets.");
        } else {
          setWidgetInstalled(false);
          toast.success("Shakatah uninstalled successfully");
          setShowUninstallDialog(false);
        }
      } else {
        toast.error(data?.error || "Failed to uninstall");
      }
    } catch (error: any) {
      console.error("Error uninstalling:", error);
      toast.error(error.message || "Failed to uninstall");
    } finally {
      setUninstallingWidget(false);
    }
  };

  const handleConnectShopify = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const targetShopifyDomain = normalizeShopifyDomain(shopifyStoreDomainInput || storeDomain);

    if (!isValidShopifyDomain(targetShopifyDomain)) {
      console.error("[Shakatah] Invalid storeDomain:", storeDomain, "input:", shopifyStoreDomainInput);
      toast.error("Enter a valid Shopify domain like your-store.myshopify.com");
      return;
    }
    
    setConnecting(true);
    
    // Use the edge function to initiate OAuth - it has the correct scopes and credentials
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const oauthInitUrl = `${supabaseUrl}/functions/v1/shopify-oauth-init?shop=${encodeURIComponent(targetShopifyDomain)}&merchant_id=${merchantId}&source=dashboard&return_to=dashboard&signup_source=direct_website`;
    
    console.log("[Shakatah] storeDomain:", targetShopifyDomain);
    console.log("[Shakatah] Opening OAuth via edge function:", oauthInitUrl);
    
    toast.info("Redirecting to Shopify authorization...");
    openShopifyTopLevel(oauthInitUrl);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const { data: merchant, error: fetchError } = await supabase
        .from('merchants')
        .select('shopify_access_token, shopify_store_domain')
        .eq('id', merchantId)
        .single();

      if (fetchError || !merchant?.shopify_access_token) {
        setTestResult({ success: false, message: "No access token configured" });
        return;
      }

      const response = await fetch(
        `https://${merchant.shopify_store_domain}/admin/api/2024-01/shop.json`,
        {
          headers: {
            'X-Shopify-Access-Token': merchant.shopify_access_token,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTestResult({ 
          success: true, 
          message: `Connected to "${data.shop.name}"` 
        });
      } else if (response.status === 401) {
        setTestResult({ 
          success: false, 
          message: "Connection expired - please reconnect" 
        });
        setHasToken(false);
      } else {
        setTestResult({ 
          success: false, 
          message: `Connection failed (${response.status})` 
        });
      }
    } catch (error) {
      console.error("Connection test error:", error);
      setTestResult({ 
        success: false, 
        message: "Network error - could not reach Shopify" 
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Store Information
            </CardTitle>
            <CardDescription>
              Your Shopify store and business details
            </CardDescription>
          </div>
          {canEdit && !editingBusinessInfo && (
            <Button variant="outline" size="sm" onClick={() => setEditingBusinessInfo(true)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Store Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Store</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground text-sm">Store Name</Label>
                <p className="font-medium">{storeName}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Store Domain</Label>
                <p className="font-medium">{storeDomain}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Store Category</Label>
                <p className="font-medium">{businessInfo.store_category ? getCategoryLabel(businessInfo.store_category) : "—"}</p>
              </div>
            </div>
          </div>

          {/* Contact Person - Editable */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <User className="h-4 w-4" />
              Contact Person
            </h4>
            {editingBusinessInfo ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="contact_person_name">Contact Name *</Label>
                  <Input
                    id="contact_person_name"
                    value={businessInfo.contact_person_name}
                    onChange={(e) => setBusinessInfo(prev => ({ ...prev, contact_person_name: e.target.value }))}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person_email">Contact Email *</Label>
                  <Input
                    id="contact_person_email"
                    type="email"
                    value={businessInfo.contact_person_email}
                    onChange={(e) => setBusinessInfo(prev => ({ ...prev, contact_person_email: e.target.value }))}
                    placeholder="john@yourstore.com"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="contact_person_phone">Contact Phone (Optional)</Label>
                  <Input
                    id="contact_person_phone"
                    type="tel"
                    value={businessInfo.contact_person_phone}
                    onChange={(e) => setBusinessInfo(prev => ({ ...prev, contact_person_phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground text-sm">Name</Label>
                  <p className="font-medium">{businessInfo.contact_person_name || "—"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Email</Label>
                  <p className="font-medium">{businessInfo.contact_person_email || "—"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Phone</Label>
                  <p className="font-medium">{businessInfo.contact_person_phone || "—"}</p>
                </div>
              </div>
            )}
          </div>

          {/* Business Details - Editable */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Business Details
            </h4>
            {editingBusinessInfo ? (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="business_email">Business Email</Label>
                    <Input
                      id="business_email"
                      type="email"
                      value={businessInfo.business_email}
                      onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_email: e.target.value }))}
                      placeholder="billing@yourstore.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_phone">Business Phone</Label>
                    <Input
                      id="business_phone"
                      type="tel"
                      value={businessInfo.business_phone}
                      onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business_address">Business Address</Label>
                  <Textarea
                    id="business_address"
                    value={businessInfo.business_address}
                    onChange={(e) => setBusinessInfo(prev => ({ ...prev, business_address: e.target.value }))}
                    placeholder="Full business address"
                    rows={2}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {GST_MODULE_ENABLED ? (
                  <div className="space-y-2">
                    <Label htmlFor="gst_number">GST Number</Label>
                    <Input
                      id="gst_number"
                      value={businessInfo.gst_number}
                      onChange={(e) => setBusinessInfo(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground text-sm">Email</Label>
                  <p className="font-medium">{businessInfo.business_email || "—"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Phone</Label>
                  <p className="font-medium">{businessInfo.business_phone || "—"}</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-muted-foreground text-sm">Address</Label>
                  <p className="font-medium">{businessInfo.business_address || "—"}</p>
                </div>
                {GST_MODULE_ENABLED ? (
                <div>
                  <Label className="text-muted-foreground text-sm">GST Number</Label>
                  <p className="font-medium">{businessInfo.gst_number || "—"}</p>
                </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Edit Actions */}
          {editingBusinessInfo && (
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingBusinessInfo(false);
                  fetchBusinessInfo(); // Reset to original values
                }}
                disabled={savingBusinessInfo}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveBusinessInfo}
                disabled={savingBusinessInfo || !businessInfo.contact_person_name || !businessInfo.contact_person_email}
              >
                {savingBusinessInfo && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Shopify Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Shopify Connection
            {hasToken ? (
              <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                <CheckCircle className="h-3 w-3 mr-1" />
                Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="text-yellow-600 border-yellow-500/50">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Not Connected
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {hasToken 
              ? "Your store is connected for automatic inventory management and order creation"
              : "Connect your store to enable split payments"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasToken ? (
            <Tabs defaultValue="oauth" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="oauth">One-Click Connect</TabsTrigger>
                <TabsTrigger value="manual">Manual Token</TabsTrigger>
              </TabsList>
              
              <TabsContent value="oauth" className="space-y-4 pt-4">
                <Alert className="border-blue-500/50 bg-blue-500/10">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-600">
                    <strong>Pre-approval required:</strong> Your store must be approved by Shakatah before connecting.
                  </AlertDescription>
                </Alert>

                <div className="text-center py-4 space-y-4">
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <Store className="h-8 w-8 text-primary" />
                  </div>
                  <div className="mx-auto max-w-md space-y-2 text-left">
                    <Label htmlFor="shopify_store_domain">Shopify store domain</Label>
                    <Input
                      id="shopify_store_domain"
                      value={shopifyStoreDomainInput}
                      onChange={(e) => setShopifyStoreDomainInput(e.target.value)}
                      placeholder="your-store.myshopify.com"
                      disabled={!canEdit || connecting}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <p className="text-xs text-muted-foreground">
                      If this merchant currently started on WooCommerce, enter the Shopify `.myshopify.com` domain here before connecting.
                    </p>
                  </div>
                  {canEdit && (
                    <Button 
                      onClick={handleConnectShopify}
                      disabled={connecting}
                      size="lg"
                      className="gap-2"
                    >
                      {connecting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4" />
                      )}
                      Connect Shopify Store
                    </Button>
                  )}
                </div>

                <div className="p-4 rounded-lg border bg-muted/50">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    What Shakatah will access
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Products:</strong> Enable split payments on specific products</li>
                    <li>• <strong>Inventory:</strong> Reserve inventory when split payment starts</li>
                    <li>• <strong>Draft Orders:</strong> Create orders when payment completes</li>
                    <li>• <strong>Orders:</strong> Track fulfillment status</li>
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="manual" className="space-y-4 pt-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Use this method if you haven't been pre-approved, or prefer to manage your own API credentials.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="settings_shop_domain">Shopify store domain *</Label>
                    <Input
                      id="settings_shop_domain"
                      value={shopifyStoreDomainInput}
                      onChange={(e) => setShopifyStoreDomainInput(e.target.value)}
                      placeholder="your-store.myshopify.com"
                      disabled={!canEdit}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="settings_access_token">Shopify Admin API Access Token *</Label>
                    <div className="relative">
                      <Input
                        id="settings_access_token"
                        type={showManualToken ? "text" : "password"}
                        value={manualToken}
                        onChange={(e) => setManualToken(e.target.value)}
                        placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="pr-10"
                        disabled={!canEdit}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowManualToken(!showManualToken)}
                      >
                        {showManualToken ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {canEdit && (
                    <Button 
                      onClick={async () => {
                        const targetShopifyDomain = normalizeShopifyDomain(shopifyStoreDomainInput || storeDomain);
                        if (!isValidShopifyDomain(targetShopifyDomain)) {
                          toast.error("Enter a valid Shopify domain like your-store.myshopify.com");
                          return;
                        }
                        if (!manualToken.trim()) {
                          toast.error("Please enter an access token");
                          return;
                        }
                        setSavingToken(true);
                        try {
                          const testResponse = await fetch(
                            `https://${targetShopifyDomain}/admin/api/2024-01/shop.json`,
                            {
                              headers: {
                                "X-Shopify-Access-Token": manualToken.trim(),
                                "Content-Type": "application/json",
                              },
                            }
                          );
                          
                          if (!testResponse.ok) {
                            if (testResponse.status === 401) {
                              toast.error("Invalid token - please check and try again");
                            } else {
                              toast.error(`Connection failed (${testResponse.status})`);
                            }
                            return;
                          }

                          const shopData = await testResponse.json();
                          
                          const { error } = await supabase
                            .from("merchants")
                            .update({
                              billing_provider: "shopify",
                              platform_type: "shopify",
                              shopify_access_token: manualToken.trim(),
                              shopify_store_domain: targetShopifyDomain,
                              signup_source: "direct_website",
                            })
                            .eq("id", merchantId);

                          if (error) throw error;

                          setHasToken(true);
                          setManualToken("");
                          toast.success(`Connected to "${shopData.shop.name}"!`);
                        } catch (error) {
                          console.error("Error saving token:", error);
                          toast.error("Failed to save token");
                        } finally {
                          setSavingToken(false);
                        }
                      }}
                      disabled={savingToken || !manualToken.trim()}
                      className="w-full"
                    >
                      {savingToken ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Key className="h-4 w-4 mr-2" />
                      )}
                      Connect Store
                    </Button>
                  )}
                </div>

                <div className="p-4 rounded-lg border bg-muted/50 space-y-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary" />
                    How to get your Access Token
                  </h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Go to Shopify Admin → Settings → Apps → Develop apps</li>
                    <li>Create app, configure Admin API scopes</li>
                    <li>Enable required scopes and install</li>
                    <li>Copy the Admin API access token</li>
                  </ol>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                    <Store className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{(shopifyStoreDomainInput || storeDomain).replace('.myshopify.com', '')}</p>
                    <p className="text-sm text-muted-foreground">Connected</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-green-600 border-green-500/50">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </Badge>
              </div>

              {testResult && (
                <Alert variant={testResult.success ? "default" : "destructive"}>
                  {testResult.success ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  <AlertDescription>{testResult.message}</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
        {hasToken && (
          <CardFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
            {canEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleConnectShopify}
                    disabled={connecting}
                    className="relative"
                  >
                    {connecting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Reconnect
                        {!hasToken && (
                          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
                        )}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                {!hasToken && (
                  <TooltipContent>
                    <p>Store connection needs update - click to reconnect</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </CardFooter>
        )}
      </Card>

      {/* Splitkart Payment Settings */}
      {hasToken && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Splitkart Payment Settings
            </CardTitle>
            <CardDescription>
              Choose the default payment gateway for split payments on your store
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <Label>Default Payment Provider</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={savingPaymentProvider || !canEdit}
                  onClick={() => handleSavePaymentProvider('razorpay')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    splitkartPaymentProvider === 'razorpay'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/50'
                  } ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Razorpay</p>
                      <p className="text-sm text-muted-foreground">Best for India (INR)</p>
                    </div>
                    {splitkartPaymentProvider === 'razorpay' && (
                      <CheckCircle className="h-5 w-5 text-primary" />
                    )}
                  </div>
                </button>
                <button
                  type="button"
                  disabled={savingPaymentProvider || !canEdit}
                  onClick={() => handleSavePaymentProvider('stripe')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    splitkartPaymentProvider === 'stripe'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/50'
                  } ${!canEdit ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Stripe</p>
                      <p className="text-sm text-muted-foreground">Best for global payments</p>
                    </div>
                    {splitkartPaymentProvider === 'stripe' && (
                      <CheckCircle className="h-5 w-5 text-primary" />
                    )}
                  </div>
                </button>
              </div>
              {savingPaymentProvider && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                This setting determines which payment gateway is used when customers pay their share during split payments.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Uninstall Shakatah - super_admin only */}
      {widgetInstalled && isSuperAdmin && (
        <div className="pt-4 border-t border-muted">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-muted-foreground">Uninstall Shakatah</p>
              <p className="text-xs text-muted-foreground">Remove Shakatah from your store</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleUninstallClick}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Uninstall
            </Button>
          </div>
        </div>
      )}

      {/* Uninstall Dialog */}
      <AlertDialog open={showUninstallDialog} onOpenChange={setShowUninstallDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Uninstall Shakatah?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>Before you uninstall, here's a summary of your Shakatah journey:</p>
                
                {loadingStats ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : uninstallStats && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50 border">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="h-4 w-4 text-primary" />
                        <span className="text-xs text-muted-foreground">Total Orders</span>
                      </div>
                      <p className="text-xl font-bold">{uninstallStats.totalRequests}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-xs text-muted-foreground">Completed</span>
                      </div>
                      <p className="text-xl font-bold text-green-600">{uninstallStats.completedRequests}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className="h-4 w-4 text-green-600" />
                        <span className="text-xs text-muted-foreground">Revenue</span>
                      </div>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(uninstallStats.totalRevenue, merchantCurrency)}</p>
                    </div>
                    {uninstallStats.pendingRequests > 0 && (
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="h-4 w-4 text-yellow-600" />
                          <span className="text-xs text-muted-foreground">Pending</span>
                        </div>
                        <p className="text-xl font-bold text-yellow-600">{uninstallStats.pendingRequests}</p>
                      </div>
                    )}
                  </div>
                )}

                {uninstallStats && uninstallStats.pendingRequests > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      You have <strong>{uninstallStats.pendingRequests} pending payment{uninstallStats.pendingRequests > 1 ? 's' : ''}</strong> worth {formatCurrency(uninstallStats.pendingRevenue, merchantCurrency)}.
                    </AlertDescription>
                  </Alert>
                )}

                {currentPlan && currentPlan !== 'trial' && (
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex items-start gap-3">
                      <ArrowDownCircle className="h-5 w-5 text-primary mt-0.5" />
                      <div className="space-y-1">
                        <p className="font-medium text-sm">Consider changing your plan instead?</p>
                        <p className="text-xs text-muted-foreground">
                          Switch to a different plan instead of uninstalling completely.
                        </p>
                        {onNavigateToBilling && (
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="h-auto p-0 text-primary"
                            onClick={() => { setShowUninstallDialog(false); onNavigateToBilling(); }}
                          >
                            View plan options →
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  You can reinstall anytime.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="sm:mr-auto">Keep Shakatah</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUninstallWidget}
              disabled={uninstallingWidget}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {uninstallingWidget ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Uninstall
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MerchantSettings;

function normalizeShopifyDomain(value?: string | null): string {
  const normalized = (value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");

  if (!normalized) {
    return "";
  }

  if (normalized.endsWith(".myshopify.com")) {
    return normalized;
  }

  return `${normalized}.myshopify.com`;
}

function isValidShopifyDomain(value: string): boolean {
  return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(value);
}
